import {
  Ajv2020,
  type ErrorObject,
  type ValidateFunction,
} from "ajv/dist/2020.js";

import appConfigSchema from "../app-config.schema.json" with { type: "json" };
import versionPolicySchema from "../version-policy.schema.json" with { type: "json" };
import type { AppConfigDocument } from "./generated/app-config-document.js";
import type { VersionPolicyDocument } from "./generated/version-policy-document.js";

const ajv = new Ajv2020({
  allErrors: true,
  strict: true,
});
ajv.addFormat("uri", {
  type: "string",
  validate: (value: string) => {
    try {
      new URL(value);
      return true;
    } catch {
      return false;
    }
  },
});

const appConfigValidator = ajv.compile<AppConfigDocument>(appConfigSchema);
const versionPolicyValidator = ajv.compile<VersionPolicyDocument>(versionPolicySchema);

const validationMessage = (
  documentName: string,
  errors: ErrorObject[] | null | undefined,
): string => {
  const detail = (errors ?? [])
    .map((error) => {
      const path = error.instancePath || "/";
      return `${path} ${error.message ?? "is invalid"}`;
    })
    .join("; ");
  return `${documentName} does not match its v1 schema${detail ? `: ${detail}` : ""}`;
};

function assertWith<T>(
  input: unknown,
  validator: ValidateFunction<T>,
  documentName: string,
  ErrorType: new (message: string) => Error,
): asserts input is T {
  if (!validator(input)) {
    throw new ErrorType(validationMessage(documentName, validator.errors));
  }
}

export function assertAppConfigStructure(
  input: unknown,
  ErrorType: new (message: string) => Error,
): asserts input is AppConfigDocument {
  assertWith(input, appConfigValidator, "appConfig", ErrorType);
}

export function assertVersionPolicyStructure(
  input: unknown,
  ErrorType: new (message: string) => Error,
): asserts input is VersionPolicyDocument {
  assertWith(input, versionPolicyValidator, "policy", ErrorType);
}
